import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import "./EditUserModal.css";
import axios from "axios";

const EditUserModal = ({ show, onHide, onSubmit, user }) => {
  const [formData, setFormData] = useState({
    email: "",
    firstName: "",
    lastName: "",
    statut: "",
    ville: "",
    concessions_id: "",
    siret: "",
    siren: "",
    telephone: "",
    service_principal: "",
  });

  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [concessions, setConcessions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [newCredentials, setNewCredentials] = useState(null);
  const [isGeneratingPassword, setIsGeneratingPassword] = useState(false);
  const [copySuccess, setCopySuccess] = useState({
    identifier: false,
    password: false
  });
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5002/api";

  useEffect(() => {
    document.body.classList.add('modal-open');
    
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, []);

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email || "",
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        statut: user.statut || "",
        ville: user.ville || "",
        concessions_id: user.concessions_id || "",
        siret: user.siret || "",
        siren: user.siren || "",
        telephone: user.telephone || "",
        service_principal: user.service_principal || "",
      });
      setError(null);
      setFieldErrors({});
      setSubmitSuccess(false);
      setNewCredentials(null);
    }
  }, [user]);

  useEffect(() => {
    const fetchConcessions = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_URL}/concessions`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.data) {
          const filteredConcessions = response.data.filter(
            (concession) => concession.groupe_id === user.groupe_id
          );

          setConcessions(filteredConcessions);
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des concessions:", error);
        setError("Erreur lors de la récupération des concessions");
      }
    };

    fetchConcessions();
  }, [API_URL, user.groupe_id]);

  useEffect(() => {
    const selectedConcession = concessions.find(
      (c) => c.id === parseInt(formData.concessions_id)
    );
  
    if (selectedConcession) {
      const siret = selectedConcession.siret || "";
      const siren = siret.substring(0, 9);
  
      setFormData(prev => ({
        ...prev,
        siret,
        siren
      }));
    }
  }, [formData.concessions_id, concessions]);
  

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (fieldErrors[name]) {
      setFieldErrors({
        ...fieldErrors,
        [name]: null
      });
    }
    
    setError(null);
    setSubmitSuccess(false);
    
    if (name === 'telephone' || name === 'siret' || name === 'siren' || name === 'code_postal') {
      if (value !== '' && !/^\d+$/.test(value)) {
        setFieldErrors({
          ...fieldErrors,
          [name]: `Le champ ${name} ne doit contenir que des chiffres`
        });
        return;
      }
      
      if (name === 'telephone') {
        if (value.length > 10) {
          return;
        }
        if (value.length > 0 && value.length < 10) {
          setFieldErrors({
            ...fieldErrors,
            [name]: "Le numéro de téléphone doit contenir 10 chiffres"
          });
        } else {
          setFieldErrors({
            ...fieldErrors,
            [name]: null
          });
        }
      }
      
      if (name === 'siret') {
        if (value.length > 14) {
          return;
        }
        if (value.length > 0 && value.length < 14) {
          setFieldErrors({
            ...fieldErrors,
            [name]: "Le SIRET doit contenir 14 chiffres"
          });
        } else {
          setFieldErrors({
            ...fieldErrors,
            [name]: null
          });
        }
      }
      
      if (name === 'siren') {
        if (value.length > 9) {
          return;
        }
        if (value.length > 0 && value.length < 9) {
          setFieldErrors({
            ...fieldErrors,
            [name]: "Le SIREN doit contenir 9 chiffres"
          });
        } else {
          setFieldErrors({
            ...fieldErrors,
            [name]: null
          });
        }
      }
      
      if (name === 'code_postal' && value.length > 5) {
        return;
      }
    }
    
    if (name === 'email') {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (value && !emailRegex.test(value)) {
        setFieldErrors({
          ...fieldErrors,
          [name]: "Format d'email invalide"
        });
      } else {
        setFieldErrors({
          ...fieldErrors,
          [name]: null
        });
      }
    }
    
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const errors = {};
    let isValid = true;
    
    if (!formData.email) {
      errors.email = "L'email est requis";
      isValid = false;
    } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email)) {
      errors.email = "L'email est invalide";
      isValid = false;
    }
    
    if (!formData.firstName || formData.firstName.trim() === "") {
      errors.firstName = "Le prénom est requis";
      isValid = false;
    }
    
    if (!formData.lastName || formData.lastName.trim() === "") {
      errors.lastName = "Le nom est requis";
      isValid = false;
    }
    
    if (!formData.statut) {
      errors.statut = "Le statut est requis";
      isValid = false;
    }
    
    if (!formData.ville || formData.ville.trim() === "") {
      errors.ville = "La ville est requise";
      isValid = false;
    }
    
    if (!formData.siret) {
      errors.siret = "Le SIRET est requis";
      isValid = false;
    } else if (formData.siret.length !== 14) {
      errors.siret = "Le SIRET doit contenir 14 chiffres";
      isValid = false;
    }
    
    if (!formData.siren) {
      errors.siren = "Le SIREN est requis";
      isValid = false;
    } else if (formData.siren.length !== 9) {
      errors.siren = "Le SIREN doit contenir 9 chiffres";
      isValid = false;
    }
    
    if (!formData.telephone) {
      errors.telephone = "Le téléphone est requis";
      isValid = false;
    } else if (formData.telephone.length !== 10) {
      errors.telephone = "Le téléphone doit contenir 10 chiffres";
      isValid = false;
    }
    
    if (!formData.concessions_id) {
      errors.concessions_id = "La concession est requise";
      isValid = false;
    }
    
    setFieldErrors(errors);
    
    if (!isValid) {
      setError("Veuillez corriger les erreurs dans le formulaire");
    }
    
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const dataToSubmit = {
        ...formData,
        id: user.id
      };
      
      try {
        const result = await onSubmit(dataToSubmit);
        
        if (result && result.success) {
          setSubmitSuccess(true);
          setError(null);
          setFieldErrors({});
          
          setTimeout(() => {
            onHide();
          }, 1500);
        } else if (result && !result.success && result.error) {
          const errorData = result.error;
          
          if (errorData.errorType === 'DUPLICATE_EMAIL') {
            setFieldErrors({
              ...fieldErrors,
              email: "Cet email est déjà utilisé par un autre utilisateur"
            });
            setError("Cet email est déjà utilisé");
          } else if (errorData.errorType === 'DUPLICATE_TELEPHONE') {
            setFieldErrors({
              ...fieldErrors,
              telephone: "Ce numéro de téléphone est déjà utilisé par un autre utilisateur"
            });
            setError("Ce numéro de téléphone est déjà utilisé");
          } else if (errorData.errorType === 'FORBIDDEN') {
            setError("Vous n'avez pas les permissions nécessaires pour modifier cet utilisateur");
          } else if (errorData.field) {
            setFieldErrors({
              ...fieldErrors,
              [errorData.field]: errorData.message || "Champ invalide"
            });
            setError(errorData.message || "Veuillez corriger le formulaire");
          } else {
            setError(errorData.message || "Erreur lors de la modification de l'utilisateur");
          }
        }
      } catch (error) {
        console.error("Erreur lors de la modification de l'utilisateur:", error);
        
        setError("Erreur lors de la modification de l'utilisateur. Veuillez réessayer.");
      }
    } catch (error) {
      console.error("Erreur inattendue:", error);
      setError("Une erreur inattendue s'est produite. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGeneratePassword = async () => {
    if (!user || !user.id) return;
    
    if (!window.confirm("Êtes-vous sûr de vouloir générer un nouveau mot de passe pour cet utilisateur ? Cette action ne peut pas être annulée.")) {
      return;
    }

    setIsGeneratingPassword(true);
    setError(null);
    
    try {
      const response = await axios.post(
        `${API_URL}/users/${user.id}/generate-password`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data && response.data.success) {
        setNewCredentials({
          identifier: response.data.identifier,
          tempPassword: response.data.tempPassword
        });
      }
    } catch (error) {
      console.error("Erreur lors de la génération du mot de passe:", error);
      if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError("Erreur lors de la génération du mot de passe. Veuillez réessayer.");
      }
    } finally {
      setIsGeneratingPassword(false);
    }
  };

  const handleCopyToClipboard = (text, type) => {
    if (text) {
      navigator.clipboard.writeText(text);
      setCopySuccess({
        ...copySuccess,
        [type]: true
      });
      
      setTimeout(() => {
        setCopySuccess(prev => ({
          ...prev,
          [type]: false
        }));
      }, 3000);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target.className === 'modal-overlay') {
      onHide();
    }
  };

  if (!show) return null;

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content">
        <div className="modal-header">
          <h2>Modifier l&apos;utilisateur</h2>
          <button className="close-button" onClick={onHide}>
            ✕
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}
        {submitSuccess && <div className="success-message">Utilisateur mis à jour avec succès</div>}

        {newCredentials ? (
          <div className="modal-body">
            <div className="success-message">
              <p>Nouvelles informations de connexion à communiquer:</p>
            </div>
            
            <div className="credentials-container">
              <div className="credential-group">
                <label>Identifiant:</label>
                <div className="credential-value">
                  <input 
                    type="text" 
                    value={newCredentials.identifier || ""} 
                    readOnly 
                    className="credential-input"
                    style={{ 
                      fontFamily: 'monospace', 
                      fontSize: '1rem',
                      textTransform: 'none',
                      letterSpacing: '0.5px'
                    }}
                  />
                  <button 
                    className="copy-button"
                    onClick={() => handleCopyToClipboard(newCredentials.identifier, 'identifier')}
                  >
                    {copySuccess.identifier ? "Copié ✓" : "Copier"}
                  </button>
                </div>
              </div>
              
              <div className="credential-group">
                <label>Nouveau mot de passe:</label>
                <div className="credential-value">
                  <input 
                    type="text" 
                    value={newCredentials.tempPassword || ""} 
                    readOnly 
                    className="credential-input"
                    style={{ 
                      fontFamily: 'monospace', 
                      fontSize: '1rem',
                      textTransform: 'none',
                      letterSpacing: '0.5px'
                    }}
                  />
                  <button 
                    className="copy-button"
                    onClick={() => handleCopyToClipboard(newCredentials.tempPassword, 'password')}
                  >
                    {copySuccess.password ? "Copié ✓" : "Copier"}
                  </button>
                </div>
              </div>
              
              <div className="credential-notice">
                ⚠️ Attention: L&apos;identifiant et le mot de passe sont sensibles à la casse.
                Veuillez les copier exactement comme affichés.
              </div>
            </div>
          </div>
        ) : (
          <div className="modal-body">
            <div className="form-section">
              <div className="form-group">
                <label className="required-field">Type d&apos;utilisateur</label>
                {user?.statut === "assistante" ? (
                  <>
                    <input
                      type="hidden"
                      name="statut"
                      value="commercial"
                    />
                    <div className="readonly-field">Commercial</div>
                  </>
                ) : (
                  <select
                    name="statut"
                    value={formData.statut}
                    onChange={handleChange}
                    required
                    className={fieldErrors.statut ? "input-error" : ""}
                  >
                    <option value="">Sélectionner un statut</option>
                    <option value="commercial">Commercial</option>
                  </select>
                )}
                {fieldErrors.statut && <div className="field-error">{fieldErrors.statut}</div>}
              </div>
            </div>

            <div className="form-section">
              <div className="form-group-row">
                <div className="form-group">
                  <label className="required-field">Prénom</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    placeholder="Ex: Jean"
                    className={fieldErrors.firstName ? "input-error" : ""}
                  />
                  {fieldErrors.firstName && <div className="field-error">{fieldErrors.firstName}</div>}
                </div>

                <div className="form-group">
                  <label className="required-field">Nom</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    placeholder="Ex: Dupont"
                    className={fieldErrors.lastName ? "input-error" : ""}
                  />
                  {fieldErrors.lastName && <div className="field-error">{fieldErrors.lastName}</div>}
                </div>
              </div>

              <div className="form-group">
                <label className="required-field">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="Ex: jean.dupont@example.com"
                  className={fieldErrors.email ? "input-error" : ""}
                />
                {fieldErrors.email && <div className="field-error">{fieldErrors.email}</div>}
              </div>
              
              <div className="form-group">
                <label className="required-field">Téléphone</label>
                <input
                  type="text"
                  name="telephone"
                  value={formData.telephone}
                  onChange={handleChange}
                  required
                  placeholder="Ex: 0123456789"
                  maxLength={10}
                  className={fieldErrors.telephone ? "input-error" : ""}
                />
                {fieldErrors.telephone && <div className="field-error">{fieldErrors.telephone}</div>}
              </div>
            </div>

            <div className="form-section">
              <div className="form-group">
                <label className="required-field">Ville</label>
                <input
                  type="text"
                  name="ville"
                  value={formData.ville}
                  onChange={handleChange}
                  required
                  placeholder="Ex: Paris"
                  className={fieldErrors.ville ? "input-error" : ""}
                />
                {fieldErrors.ville && <div className="field-error">{fieldErrors.ville}</div>}
              </div>

              <div className="form-group-row">
                <div className="form-group">
                  <label className="required-field">SIRET (14 chiffres)</label>
                  <input
                    type="text"
                    name="siret"
                    value={formData.siret}
                    onChange={handleChange}
                    required
                    placeholder="Ex: 12345678901234"
                    maxLength={14}
                    className={fieldErrors.siret ? "input-error" : ""}
                  />
                  {fieldErrors.siret && <div className="field-error">{fieldErrors.siret}</div>}
                </div>

                <div className="form-group">
                  <label className="required-field">SIREN (9 chiffres)</label>
                  <input
                    type="text"
                    name="siren"
                    value={formData.siren}
                    onChange={handleChange}
                    required
                    placeholder="Ex: 123456789"
                    maxLength={9}
                    className={fieldErrors.siren ? "input-error" : ""}
                  />
                  {fieldErrors.siren && <div className="field-error">{fieldErrors.siren}</div>}
                </div>
              </div>

              <div className="form-group">
                <label className="required-field">Concession</label>
                <select
                  name="concessions_id"
                  value={formData.concessions_id}
                  onChange={handleChange}
                  required
                  className={fieldErrors.concessions_id ? "input-error" : ""}
                >
                  <option value="">Sélectionner une concession</option>
                  {concessions.map((concession) => (
                    <option key={concession.id} value={concession.id}>
                      {concession.nom ? concession.nom : concession.ville + " " + concession.siret}
                    </option>
                  ))}
                </select>
                {fieldErrors.concessions_id && <div className="field-error">{fieldErrors.concessions_id}</div>}
              </div>

              <div className="form-group">
                <label>Service Principal</label>
                <select
                  name="service_principal"
                  value={formData.service_principal}
                  onChange={handleChange}
                  className={fieldErrors.service_principal ? "input-error" : ""}
                >
                  <option value="">Sélectionner un service</option>
                  <option value="VO">Véhicules d&apos;occasion</option>
                  <option value="VN">Véhicules neufs</option>
                  <option value="AV">Autres véhicules</option>
                </select>
                {fieldErrors.service_principal && <div className="field-error">{fieldErrors.service_principal}</div>}
              </div>
            </div>

            <div className="form-section password-section">
              <button 
                type="button" 
                className="button generate-password-button" 
                onClick={handleGeneratePassword}
                disabled={isGeneratingPassword}
              >
                {isGeneratingPassword ? "Génération..." : "Générer un nouveau mot de passe"}
              </button>
              <p className="password-note">
                Cette action générera un nouveau mot de passe et invalidera l&apos;ancien.
              </p>
            </div>
          </div>
        )}

        <div className="modal-footer">
          <button type="button" className="button secondary" onClick={onHide}>
            {newCredentials ? "Fermer" : "Annuler"}
          </button>
          {!newCredentials && (
            <button 
              type="button" 
              className="button primary" 
              onClick={handleSubmit}
              disabled={isSubmitting || submitSuccess}
            >
              {isSubmitting ? "Enregistrement..." : submitSuccess ? "Enregistré" : "Enregistrer"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

EditUserModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  user: PropTypes.shape({
    id: PropTypes.number,
    email: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    statut: PropTypes.string,
    ville: PropTypes.string,
    concessions_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    siret: PropTypes.string,
    siren: PropTypes.string,
    telephone: PropTypes.string,
    service_principal: PropTypes.string,
    groupe_id: PropTypes.number,
  }),
};

export default EditUserModal;